import { MutationStatus, useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { FileUploadStatus } from '@/components/shared/FileUpload/types';
import {
  ServiceUploadingOptions,
  UploadProgress,
} from '@/utils/shared/uploadProgress';

export type UploadFile<T> = (
  file: File,
  options: ServiceUploadingOptions,
) => Promise<T>;

const INITIAL_PROGRESS: UploadProgress = { percent: 0, loaded: 0, total: 0 };

const UPLOAD_STATUS: Record<MutationStatus, FileUploadStatus | undefined> = {
  idle: undefined,
  loading: 'uploading',
  success: 'uploaded',
  error: 'failed',
};

export default function useFileUpload<T>(upload: UploadFile<T>) {
  const [progress, setProgress] = useState(INITIAL_PROGRESS);
  const controller = useRef<AbortController>();

  const {
    mutate,
    reset: resetMutation,
    variables: file,
    data: result,
    error,
    status,
  } = useMutation({
    mutationFn: (selected: File) => {
      controller.current = new AbortController();
      setProgress({ ...INITIAL_PROGRESS, total: selected.size });
      return upload(selected, {
        onUploadProgress: next =>
          setProgress(current =>
            current.percent === next.percent ? current : next,
          ),
        signal: controller.current.signal,
      });
    },
  });

  const reset = () => {
    resetMutation();
    setProgress(INITIAL_PROGRESS);
  };

  const cancel = () => {
    controller.current?.abort();
    reset();
  };

  const retry = () => {
    if (file) mutate(file);
  };

  return {
    file,
    status: UPLOAD_STATUS[status],
    progress,
    result,
    error,
    start: mutate,
    cancel,
    retry,
    reset,
  };
}
